import React from "react";
import values from "lodash/values";
import pick from "lodash/pickBy";
import groupBy from "lodash/groupBy";
import moment from "moment-timezone";
import { Link } from "react-router";
import { Checkbox, RaisedButton } from "material-ui";
import { Table, Tr, Td, Sort } from "reactable";
import { colors } from "material-ui/styles";
import assignWith from "lodash/assignWith";

import {
  formatPrice,
  clientSideDownload,
  makeCsv,
  makeEventDateRangeLabel
} from "../util";

import connectToStores from "alt-utils/lib/connectToStores";
import GodActions from "../actions/GodActions";
import GodStore from "../stores/GodStore";
import UserStore from "../stores/UserStore";

import WorkingIndicator from "./partials/working-indicator.jsx";

const _color = count =>
  count === 0
    ? "#ccc"
    : count > 1000
    ? "#f44336"
    : count > 500
    ? "#ff9800"
    : count > 100
    ? "#8bc34a"
    : "#4caf50";

class GodUpcomingEvents extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      includeEventsBeforeNow: false,
      includePhysicalEvents: false,
      groupByEventBucket: false
    };
    this.downloadCsv = this.downloadCsv.bind(this);
    this._makeRowData = this._makeRowData.bind(this);
    this._makeRowForUpcomingEvent = this._makeRowForUpcomingEvent.bind(this);
  }

  static getStores() {
    return [UserStore, GodStore];
  }

  static getPropsFromStores() {
    return {
      upcomingEvents: GodStore.getState().upcomingEvents,
      condensedUsers: UserStore.getState().condensedUsers
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  componentDidMount() {
    GodActions.fetchUpcomingEvents.defer();
  }

  getStyles() {
    return {};
  }

  downloadCsv() {
    clientSideDownload(
      `eventive-upcoming-events-${new Date().toISOString()}.csv`,
      "text/csv",
      makeCsv(
        this._makeRowData().map(upcomingEvent => {
          const user = this.props.condensedUsers.find(
            user => user.id === upcomingEvent._owner
          ) || {
            organization: "-"
          };
          return {
            "Organization ID": user.id,
            Organization: user.organization,
            "Event Bucket": upcomingEvent.event_bucket_name,
            Event: upcomingEvent.name,
            "Start Date": upcomingEvent.start_time,
            "End Date": upcomingEvent.end_time,
            Type: this.state.groupByEventBucket
              ? "Event Bucket"
              : upcomingEvent.is_virtual
              ? "Virtual"
              : "Physical",
            "Tickets Issued": upcomingEvent.tickets_issued,
            ...(this.state.groupByEventBucket
              ? {
                  "Passes Issued": upcomingEvent.passes_issued
                }
              : null)
          };
        })
      )
    );
  }

  _makeRowData() {
    let events = this.props.upcomingEvents.upcoming_events.filter(e => {
      if (
        !this.state.includeEventsBeforeNow &&
        Date.now() - new Date(e.start_time) > 1000 * 60 * 60 * 24 * 30
      ) {
        return false;
      }
      if (this.state.includePhysicalEvents) {
        return true;
      } else {
        return !!e.is_virtual;
      }
    });

    if (this.state.groupByEventBucket) {
      const eventsByEventBucket = groupBy(events, "event_bucket");
      return Object.keys(eventsByEventBucket).map(eventBucketId => {
        const events = eventsByEventBucket[eventBucketId];
        let start_time = Number.MAX_VALUE,
          end_time = Number.MIN_VALUE,
          tickets_issued = 0;
        for (const e of events) {
          if (new Date(e.start_time) < start_time) {
            start_time = e.start_time;
          }
          if (new Date(e.end_time) > end_time) {
            end_time = e.end_time;
          }
          tickets_issued += e.tickets_issued;
        }
        return {
          _owner: events[0]._owner,
          event_bucket: events[0].event_bucket,
          event_bucket_name: events[0].event_bucket_name,
          start_time,
          end_time,
          tickets_issued,
          passes_issued:
            this.props.upcomingEvents.passes_issued_by_event_bucket[
              eventBucketId
            ] || 0,
          is_virtual: events.some(e => e.is_virtual),
          is_dated: true,
          timezone: moment.tz.guess()
        };
      });
    }

    return events.map(e => {
      return {
        ...e,
        is_dated: true,
        timezone: moment.tz.guess()
      };
    });
  }

  _makeRowForUpcomingEvent(upcomingEvent) {
    const user = this.props.condensedUsers.find(
      user => user.id === upcomingEvent._owner
    ) || {
      organization: "-"
    };

    return (
      <Tr>
        <Td column="Organization" value={user.organization}>
          <a
            href={`/organization_health?_god_become_user=${user.id}`}
            target="_blank"
          >
            {user.organization}
          </a>
        </Td>
        <Td column="Event Bucket" value={upcomingEvent.event_bucket_name}>
          <span>
            <a
              href={`/event_buckets/${upcomingEvent.event_bucket}?_god_become_user=${user.id}`}
              target="_blank"
            >
              {upcomingEvent.event_bucket_name}
            </a>
          </span>
        </Td>
        <Td column="Event" value={upcomingEvent.name}>
          <span>
            <a
              href={`/event_buckets/${upcomingEvent.event_bucket}/events/${upcomingEvent._id}?_god_become_user=${user.id}`}
              target="_blank"
            >
              {upcomingEvent.name}
            </a>
            {upcomingEvent.is_virtual ? "" : "*"}
          </span>
        </Td>
        <Td column="Date" value={upcomingEvent.start_time || "-"}>
          <span
            style={{
              whiteSpace: "nowrap",
              fontStyle: upcomingEvent.is_virtual ? "" : "italic"
            }}
          >
            {makeEventDateRangeLabel({ ...upcomingEvent, is_virtual: false })}
          </span>
        </Td>
        <Td
          column="Tickets Issued"
          value={upcomingEvent.tickets_issued}
          style={{
            backgroundColor: _color(upcomingEvent.tickets_issued),
            color: "#fff",
            borderBottom: "1px solid #fff",
            fontStyle: upcomingEvent.is_virtual ? "" : "italic"
          }}
        >
          <span>
            {upcomingEvent.tickets_issued.toLocaleString()}
            {upcomingEvent.is_virtual ? "" : "*"}
          </span>
        </Td>
        <Td
          column="Passes Issued"
          value={upcomingEvent.passes_issued}
          style={{
            backgroundColor: _color(upcomingEvent.passes_issued),
            color: "#fff",
            borderBottom: "1px solid #fff",
            borderLeft: "1px solid #fff"
          }}
        >
          <span>
            {upcomingEvent.passes_issued &&
              upcomingEvent.passes_issued.toLocaleString()}
          </span>
        </Td>
      </Tr>
    );
  }

  render() {
    let styles = this.getStyles();

    if (!this.props.upcomingEvents || !this.props.condensedUsers) {
      return <WorkingIndicator fullPage={true} />;
    }

    const rows = this._makeRowData().map(this._makeRowForUpcomingEvent);

    return (
      <div>
        <h1>God Upcoming Events with &gt;10 tickets issued</h1>
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <RaisedButton
            onClick={() => this.downloadCsv()}
            label={`Download all upcoming ${
              this.state.groupByEventBucket ? "event buckets" : "events"
            } (.CSV)`}
          />
        </div>
        <div style={{ marginBottom: 10 }}>
          <Checkbox
            label={`Include physical events${
              this.state.includePhysicalEvents ? " (marked with *)" : ""
            }`}
            value={this.state.includePhysicalEvents}
            style={{ width: 310 }}
            onCheck={e =>
              this.setState({
                includePhysicalEvents: !this.state.includePhysicalEvents
              })
            }
          />
          <Checkbox
            label={`Group by event bucket`}
            value={this.state.groupByEventBucket}
            style={{ width: 310 }}
            onCheck={e =>
              this.setState({
                groupByEventBucket: !this.state.groupByEventBucket
              })
            }
          />
          <Checkbox
            label={`Include events that started over a month ago`}
            value={this.state.includeEventsBeforeNow}
            style={{ width: 340 }}
            onCheck={e =>
              this.setState({
                includeEventsBeforeNow: !this.state.includeEventsBeforeNow
              })
            }
          />
        </div>
        <Table
          className="bordered striped responsive-table"
          columns={["Organization", "Event Bucket"]
            .concat(this.state.groupByEventBucket ? [] : ["Event"])
            .concat(
              [
                { key: "Date", label: `Date (${moment.tz.guess()})` },
                "Tickets Issued"
              ].concat(this.state.groupByEventBucket ? ["Passes Issued"] : [])
            )}
          sortable={[
            "Organization",
            "Event Bucket",
            "Event",
            "Date",
            "Tickets Issued",
            "Passes Issued"
          ]}
          defaultSort={{ column: "Date", direction: "asc" }}
          filterable={["Organization", "Event Bucket", "Event", "Date"]}
          filterPlaceholder="Type to filter..."
        >
          {rows}
        </Table>
      </div>
    );
  }
}

export default connectToStores(GodUpcomingEvents);



// WEBPACK FOOTER //
// ./src/app/components/god-upcoming-events.jsx